import {
  BodyText,
  Card,
  DetailText,
  Flex,
} from '@utilitywarehouse/hearth-react';
import clsx from 'clsx';
import { memo, useMemo } from 'react';

import { BrandLogo } from '@features/Cashback/Calculator/BrandLogo/BrandLogo';
import { useCashbackCalculatorContext } from '@features/Cashback/Calculator/CalculatorProvider/CalculatorContext';
import { categories } from '@features/Cashback/Calculator/constants/categories';
import {
  type CalculationOutputCategory,
  type Category,
} from '@features/Cashback/Calculator/types';
import { formatNumber } from '@utils/format';

import './styles.scss';

interface CategoryProps {
  category: CalculationOutputCategory;
  scale: number;
}

const Category = memo(({ category, scale }: CategoryProps) => {
  const { retailers } = useCashbackCalculatorContext();
  const combinedRetailers = [...retailers.partners, ...retailers.nonPartners];
  const categoryConfig = categories.find((cat) => cat.id === category.category);

  if (!categoryConfig) return null;

  return (
    <Flex
      className={clsx(
        'earnCat__Category',
        `earnCat__Category--${categoryConfig.color}`
      )}
      style={{ flexBasis: `${(scale * 100).toFixed(2)}%` }}
      spacing="md"
      alignItems="start"
    >
      <Flex spacing="md" className="earnCat__CategoryContent">
        <div className="earnCat__CategoryIcon">{categoryConfig.icon}</div>
        <div className="earnCat__CategoryLabel">
          <BodyText size="md">{categoryConfig.name}</BodyText>
          <DetailText size="lg">
            £
            {formatNumber(category.earnings, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </DetailText>
        </div>
      </Flex>
      <Flex spacing="xs" asChild>
        <ul className="earnCat__CategoryRetailers">
          {category.retailers.map((retailer) => {
            const activeRetailer = combinedRetailers.find(
              (r) => r.id === retailer
            );

            if (!activeRetailer) return null;
            return (
              <li className="earnCat__CategoryRetailerItem">
                <Card
                  paddingNone
                  variant="emphasis"
                  colorScheme="neutralStrong"
                  className="earnCat__CategoryRetailerItemIcon"
                >
                  <BrandLogo brand={activeRetailer} />
                </Card>
              </li>
            );
          })}
        </ul>
      </Flex>
    </Flex>
  );
});

interface CategoriesProps {
  categories: CalculationOutputCategory[];
  earningsTotal: number;
}

const Categories = ({ categories, earningsTotal }: CategoriesProps) => {
  const exp = Math.pow(10, 2);
  const earningsCents = earningsTotal * exp;
  const scales: { [key: string]: number } = useMemo(
    () =>
      categories.reduce((acc, category) => {
        const catEarningCents = category.earnings * exp;
        return {
          ...acc,
          [category.category]: catEarningCents / earningsCents,
        };
      }, {}),
    [categories, earningsCents, exp]
  );

  const filteredCategories = categories.filter((cat) => cat.earnings > 0);

  return (
    <Flex
      direction="column"
      justifyContent="stretch"
      spacing="xs"
      className="earnCat__Container"
    >
      {filteredCategories.map((category) => (
        <Category
          key={category.category}
          category={category}
          scale={scales[category.category]}
        />
      ))}
    </Flex>
  );
};

export { Categories, type CategoriesProps };
